import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { useI18n } from "@/contexts/I18nContext";
import { isCategoryForType } from "@/i18n/categories";
import {
  ArrowLeftRight,
  Bot,
  Calendar,
  Camera,
  Loader2,
  Mic,
  MicOff,
  Plus,
  Sparkles,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";

interface Transaction {
  id: number;
  type: "income" | "expense" | "transfer";
  amount: string | number;
  description?: string | null;
  date: Date | string;
  categoryId?: number | null;
  notes?: string | null;
  goalId?: number | null;
}

interface TransactionFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  familyId?: number;
  /** Se fornecido, abre o formulário em modo de edição */
  editTransaction?: Transaction;
}

export function TransactionForm({ open, onClose, onSuccess, familyId, editTransaction }: TransactionFormProps) {
  const isEditing = !!editTransaction;
  const { t, ui, currencySymbol, categoryName } = useI18n();

  const [type, setType] = useState<"income" | "expense" | "transfer">("expense");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState<Date>(new Date());
  const [categoryId, setCategoryId] = useState<string>("");
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryParentId, setNewCategoryParentId] = useState("none");
  const [notes, setNotes] = useState("");
  const [goalId, setGoalId] = useState<string>("");
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [aiSuggestion, setAiSuggestion] = useState<string | null>(null);
  const [ocrPreview, setOcrPreview] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: categories = [] } = trpc.categories.list.useQuery({ familyId });
  const { data: goals = [] } = trpc.goals.list.useQuery({});
  const utils = trpc.useUtils();

  const createCategoryMutation = trpc.categories.create.useMutation({
    onSuccess: (data) => {
      void utils.categories.list.invalidate();
      setCategoryId(String(data.id));
      setNewCategoryName("");
      setNewCategoryParentId("none");
      setShowNewCategory(false);
      toast.success(t.settings.categories.created);
    },
    onError: (error) => toast.error(error.message),
  });

  // Preenche o formulário ao abrir em modo de edição
  useEffect(() => {
    if (open && editTransaction) {
      setType(editTransaction.type);
      setAmount(String(parseFloat(String(editTransaction.amount))));
      setDescription(editTransaction.description ?? "");
      setDate(new Date(editTransaction.date));
      setCategoryId(editTransaction.categoryId ? String(editTransaction.categoryId) : "");
      setNotes(editTransaction.notes ?? "");
      setGoalId(editTransaction.goalId ? String(editTransaction.goalId) : "");
      setAiSuggestion(null);
      setOcrPreview(null);
    } else if (open && !editTransaction) {
      resetForm();
    }
  }, [open, editTransaction]);

  const createMutation = trpc.transactions.create.useMutation({
    onSuccess: () => {
      toast.success(ui.transactionForm.created);
      invalidateAll();
      resetForm();
      onSuccess?.();
      onClose();
    },
    onError: (err) => toast.error(err.message),
  });

  const updateMutation = trpc.transactions.update.useMutation({
    onSuccess: () => {
      toast.success(ui.transactionForm.updated);
      invalidateAll();
      onSuccess?.();
      onClose();
    },
    onError: (err) => toast.error(err.message),
  });

  const invalidateAll = () => {
    utils.transactions.list.invalidate();
    utils.transactions.monthSummary.invalidate();
    utils.transactions.categorySpending.invalidate();
    utils.transactions.monthlyTotals.invalidate();
    utils.budgets.list.invalidate();
    utils.goals.list.invalidate();
  };

  const categorizeMutation = trpc.ai.categorize.useMutation({
    onSuccess: (data) => {
      if (data.confidence > 0.5) {
        setAiSuggestion(data.category);
        const cat = categories.find((c) => c.name === data.category && isCategoryForType(c, type));
        if (cat) setCategoryId(String(cat.id));
      }
    },
  });

  const quickVoiceMutation = trpc.ai.quickVoiceEntry.useMutation({
    onSuccess: (data) => {
      if (data.transcription) {
        setDescription(data.description || data.transcription);
        if (data.amount) setAmount(String(data.amount));
        const nextType = data.type ?? type;
        if (data.type) setType(data.type);
        const matchingCategory = categories.find((category) => category.name === data.category && isCategoryForType(category, nextType));
        if (matchingCategory) setCategoryId(String(matchingCategory.id));
        toast.success(ui.transactionForm.voiceSuccess);
      }
    },
    onError: () => toast.error(ui.transactionForm.voiceError),
  });

  const ocrMutation = trpc.ai.ocrReceipt.useMutation({
    onSuccess: (data) => {
      if (data.amount) setAmount(String(data.amount));
      if (data.establishment) setDescription(data.establishment);
      if (data.date) {
        const parsed = new Date(data.date);
        if (!isNaN(parsed.getTime())) setDate(parsed);
      }
      toast.success(ui.transactionForm.receiptSuccess);
    },
    onError: () => toast.error(ui.transactionForm.receiptError),
  });

  // Categorização automática ao digitar (apenas no modo criação)
  useEffect(() => {
    if (!isEditing && description.length > 3) {
      const timeout = setTimeout(() => {
        categorizeMutation.mutate({ description });
      }, 800);
      return () => clearTimeout(timeout);
    }
  }, [description, isEditing]);

  const resetForm = () => {
    setType("expense");
    setAmount("");
    setDescription("");
    setDate(new Date());
    setCategoryId("");
    setShowNewCategory(false);
    setNewCategoryName("");
    setNewCategoryParentId("none");
    setNotes("");
    setGoalId("");
    setAiSuggestion(null);
    if (ocrPreview) URL.revokeObjectURL(ocrPreview);
    setOcrPreview(null);
  };

  const handleSubmit = () => {
    const parsedAmount = parseFloat(String(amount).replace(",", "."));
    if (isNaN(parsedAmount) || parsedAmount <= 0) return toast.error(ui.transactionForm.invalidAmount);

    if (isEditing && editTransaction) {
      updateMutation.mutate({
        id: editTransaction.id,
        type,
        amount: parsedAmount,
        description: description || undefined,
        date,
        categoryId: categoryId ? parseInt(categoryId) : undefined,
        notes: notes || undefined,
      });
    } else {
      createMutation.mutate({
        type,
        amount: parsedAmount,
        description: description || undefined,
        date,
        categoryId: categoryId ? parseInt(categoryId) : undefined,
        familyId,
        notes: notes || undefined,
        aiCategorized: !!aiSuggestion,
        goalId: type === "transfer" && goalId ? parseInt(goalId) : undefined,
      });
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : MediaRecorder.isTypeSupported("audio/mp4")
            ? "audio/mp4"
            : "";
      if (!mimeType) throw new Error("unsupported-audio");
      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => chunksRef.current.push(e.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mimeType });
        if (blob.size > 16 * 1024 * 1024) {
          toast.error(ui.transactionForm.voiceError);
          setIsRecording(false);
          return;
        }
        const reader = new FileReader();
        reader.onload = async () => {
          const base64 = (reader.result as string).split(",")[1];
          quickVoiceMutation.mutate({ base64, mimeType });
        };
        reader.readAsDataURL(blob);
        toast.info(ui.transactionForm.audioCaptured);
        setIsRecording(false);
        clearInterval(timerRef.current!);
        setRecordingTime(0);
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
      timerRef.current = setInterval(() => setRecordingTime((t) => {
        if (t >= 120) {
          recorder.stop();
          return t;
        }
        return t + 1;
      }), 1000);
    } catch {
      toast.error(ui.transactionForm.microphoneDenied);
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const acceptedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!acceptedTypes.includes(file.type) || file.size > 6 * 1024 * 1024) {
      toast.error(ui.transactionForm.receiptError);
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(",")[1];
      // Usa o mesmo data URL lido pelo navegador na prévia. Isso evita
      // prévias quebradas em navegadores que bloqueiam blob: temporário.
      setOcrPreview(dataUrl);
      ocrMutation.mutate({ base64, mimeType: file.type });
    };
    reader.readAsDataURL(file);
  };

  // Filtra categorias por tipo
  const expenseCategories = categories.filter((c) => isCategoryForType(c, "expense"));
  const incomeCategories = categories.filter((c) => isCategoryForType(c, "income"));
  const transferCategories = categories.filter((c) => isCategoryForType(c, "transfer"));
  const visibleCategories =
    type === "income" ? incomeCategories :
    type === "transfer" ? transferCategories :
    expenseCategories;
  const customParentCategories = visibleCategories.filter((category) => category.userId !== null && category.parentId === null && category.kind === type);
  const displayCategoryName = (category: (typeof visibleCategories)[number]) => {
    const parent = category.parentId ? categories.find((candidate) => candidate.id === category.parentId) : null;
    return parent ? `↳ ${categoryName(parent.name)} / ${categoryName(category.name)}` : categoryName(category.name);
  };

  // Apenas metas ativas
  const activeGoals = goals.filter((g) => g.status === "active");

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">
            {isEditing ? t.transactions.edit : t.transactions.new}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Type tabs */}
          <Tabs value={type} onValueChange={(v) => {
            setType(v as "income" | "expense" | "transfer");
            setCategoryId(""); // limpa categoria ao trocar tipo
            setGoalId(""); // limpa meta ao trocar tipo
          }}>
            <TabsList className="w-full">
              <TabsTrigger value="expense" className="flex-1 data-[state=active]:bg-destructive data-[state=active]:text-destructive-foreground">
                {t.transactions.expense}
              </TabsTrigger>
              <TabsTrigger value="income" className="flex-1 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                {t.transactions.income}
              </TabsTrigger>
              <TabsTrigger value="transfer" className="flex-1 data-[state=active]:bg-blue-500 data-[state=active]:text-white">
                <ArrowLeftRight className="w-3 h-3 mr-1" />
                {t.transactions.transfer}
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Amount */}
          <div>
            <Label className="text-sm font-medium mb-1.5 block">{t.transactions.amount} ({currencySymbol})</Label>
            <Input
              type="number"
              placeholder={ui.transactionForm.amountPlaceholder}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="text-xl font-semibold h-12"
              step="0.01"
              min="0"
            />
          </div>

          {/* Description + AI */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-sm font-medium">{t.transactions.description}</Label>
              {categorizeMutation.isPending && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Loader2 className="w-3 h-3 animate-spin" /> {ui.transactionForm.aiCategorizing}
                </span>
              )}
              {aiSuggestion && !categorizeMutation.isPending && (
                <span className="flex items-center gap-1 text-xs text-primary">
                  <Sparkles className="w-3 h-3" /> {categoryName(aiSuggestion)}
                </span>
              )}
            </div>
            <div className="relative">
              <Input
                placeholder={ui.transactionForm.descriptionPlaceholder}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="pr-10"
              />
              <Bot className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            </div>
          </div>

          {/* Category */}
          <div>
            <Label className="text-sm font-medium mb-1.5 block">{t.transactions.category}</Label>
            <Select value={categoryId} onValueChange={(value) => {
              if (value === "__new_category__") {
                setShowNewCategory(true);
                setCategoryId("");
              } else {
                setShowNewCategory(false);
                setCategoryId(value);
              }
            }}>
              <SelectTrigger>
                <SelectValue placeholder={ui.transactionForm.selectCategory} />
              </SelectTrigger>
              <SelectContent>
                {visibleCategories.map((cat) => (
                  <SelectItem key={cat.id} value={String(cat.id)}>
                    {displayCategoryName(cat)}
                  </SelectItem>
                ))}
                {type !== "transfer" && <SelectItem value="__new_category__"><span className="flex items-center gap-2 text-primary"><Plus className="h-4 w-4" />{t.settings.categories.add}</span></SelectItem>}
              </SelectContent>
            </Select>
            {showNewCategory && type !== "transfer" && (
              <div className="mt-2 rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2">
                <Label className="text-xs font-medium">{t.settings.categories.subcategory}</Label>
                <Input
                  value={newCategoryName}
                  onChange={(event) => setNewCategoryName(event.target.value)}
                  placeholder={t.settings.categories.namePlaceholder}
                  aria-label={t.settings.categories.namePlaceholder}
                />
                <Select value={newCategoryParentId} onValueChange={setNewCategoryParentId}>
                  <SelectTrigger><SelectValue placeholder={t.settings.categories.parent} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t.settings.categories.noParent}</SelectItem>
                    {customParentCategories.map((parent) => <SelectItem key={parent.id} value={String(parent.id)}>{categoryName(parent.name)}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => createCategoryMutation.mutate({ name: newCategoryName.trim(), kind: type as "expense" | "income", parentId: newCategoryParentId === "none" ? null : Number(newCategoryParentId) })}
                    disabled={!newCategoryName.trim() || createCategoryMutation.isPending}
                  >{t.settings.categories.save}</Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setShowNewCategory(false)}>{t.settings.categories.cancel}</Button>
                </div>
              </div>
            )}
          </div>

          {/* Meta vinculada — apenas para transferências, apenas no modo criação */}
          {type === "transfer" && !isEditing && activeGoals.length > 0 && (
            <div>
              <Label className="text-sm font-medium mb-1.5 block">{ui.transactionForm.optionalGoal}</Label>
              <Select value={goalId || "none"} onValueChange={(v) => setGoalId(v === "none" ? "" : v)}>
                <SelectTrigger>
                <SelectValue placeholder={ui.transactionForm.noGoal} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{ui.transactionForm.noGoal}</SelectItem>
                  {activeGoals.map((g) => (
                    <SelectItem key={g.id} value={String(g.id)}>
                      {g.icon ?? "🎯"} {g.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                {ui.transactionForm.goalProgressHint}
              </p>
            </div>
          )}

          {/* Date */}
          <div>
            <Label className="text-sm font-medium mb-1.5 block">{t.transactions.date}</Label>
            <div className="relative flex items-center">
              <Calendar className="absolute left-3 w-4 h-4 text-muted-foreground pointer-events-none" />
              <input
                type="date"
                value={format(date, "yyyy-MM-dd")}
                onChange={(e) => {
                  const d = new Date(e.target.value + "T12:00:00");
                  if (!isNaN(d.getTime())) setDate(d);
                }}
                className="w-full h-9 pl-9 pr-3 rounded-md border border-input bg-transparent text-sm shadow-xs focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring"
              />
            </div>
          </div>

          {/* Voice & OCR — apenas no modo criação */}
          {!isEditing && (
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                className={cn("flex-1 gap-2", isRecording && "border-destructive text-destructive")}
                onClick={isRecording ? stopRecording : startRecording}
              >
                {isRecording ? (
                  <>
                    <MicOff className="w-4 h-4" />
                    {ui.transactionForm.recordingStop} ({recordingTime}s)
                  </>
                ) : (
                  <>
                    <Mic className="w-4 h-4" />
                    {ui.transactionForm.voice}
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="flex-1 gap-2"
                onClick={() => fileInputRef.current?.click()}
                disabled={ocrMutation.isPending}
                aria-label={ocrPreview ? ui.transactionForm.replacePhoto : ui.transactionForm.takePhoto}
              >
                {ocrMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Camera className="w-4 h-4" />
                )}
                {ocrPreview ? ui.transactionForm.replacePhoto : ui.transactionForm.takePhoto}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleFileUpload}
              />
            </div>
          )}

          {!isEditing && (
            <p className="text-xs text-muted-foreground -mt-3">
              {ui.transactionForm.receiptPrivacyHint}
            </p>
          )}

          {ocrPreview && (
            <div className="relative">
              <img src={ocrPreview} alt={ui.transactionForm.receiptAlt} className="w-full h-32 object-cover rounded-lg" />
              <button
                type="button"
                onClick={() => {
                  URL.revokeObjectURL(ocrPreview);
                  setOcrPreview(null);
                }}
                className="absolute top-2 right-2 w-11 h-11 bg-black/50 rounded-full flex items-center justify-center"
                aria-label="Remover comprovante"
              >
                <X className="w-3 h-3 text-white" />
              </button>
            </div>
          )}

          {/* Notes */}
          <div>
            <Label className="text-sm font-medium mb-1.5 block">{ui.transactionForm.optionalNotes}</Label>
            <Textarea
              placeholder={ui.transactionForm.notesPlaceholder}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1" onClick={onClose}>
              {t.transactions.cancel}
            </Button>
            <Button
              className="flex-1"
              onClick={handleSubmit}
              disabled={isPending || !amount}
            >
              {isPending ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              {isEditing ? ui.transactionForm.saveChanges : t.transactions.save}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
